// src/components/document/Ruler.tsx
// Horizontal ruler with draggable margin markers for the PagedEditor.

import { useState, useCallback, useEffect, useRef } from 'react';

const MM_TO_PX = 96 / 25.4;
const CM_TO_PX = 37.8; // 96 / 25.4 * 10
const INCH_TO_PX = 96;

export interface RulerProps {
  widthPx: number;
  marginLeftPx: number;
  marginRightPx: number;
  unit?: 'cm' | 'inch';
  visible: boolean;
  onMarginsChange?: (margins: { left_mm?: number; right_mm?: number }) => void;
}

/** Snap a pixel value to the nearest 1mm increment. */
function snapToMm(px: number): number {
  const mm = Math.round(px / MM_TO_PX);
  return mm * MM_TO_PX;
}

function pxToMm(px: number): number {
  return Math.round(px / MM_TO_PX);
}

export function Ruler({
  widthPx,
  marginLeftPx,
  marginRightPx,
  unit = 'cm',
  visible,
  onMarginsChange,
}: RulerProps) {
  const rulerRef = useRef<HTMLDivElement>(null);
  const [dragTarget, setDragTarget] = useState<'left' | 'right' | null>(null);
  const [dragLeftPx, setDragLeftPx] = useState(marginLeftPx);
  const [dragRightPx, setDragRightPx] = useState(marginRightPx);

  // Sync state when props change and not dragging
  useEffect(() => {
    if (!dragTarget) {
      setDragLeftPx(marginLeftPx);
      setDragRightPx(marginRightPx);
    }
  }, [marginLeftPx, marginRightPx, dragTarget]);

  const currentLeft = dragTarget === 'left' ? dragLeftPx : marginLeftPx;
  const currentRight = dragTarget === 'right' ? dragRightPx : marginRightPx;

  // ------ Drag logic ------
  const dragStartX = useRef(0);
  const dragStartVal = useRef(0);

  const handleMouseDown = useCallback(
    (side: 'left' | 'right', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget(side);
      dragStartX.current = e.clientX;
      dragStartVal.current = side === 'left' ? marginLeftPx : marginRightPx;
    },
    [marginLeftPx, marginRightPx],
  );

  useEffect(() => {
    if (!dragTarget) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - dragStartX.current;
      // Account for the ruler's scale — get actual rendered width vs logical width
      const rulerEl = rulerRef.current;
      let scaleFactor = 1;
      if (rulerEl) {
        const rect = rulerEl.getBoundingClientRect();
        scaleFactor = widthPx / rect.width;
      }
      const scaledDelta = delta * scaleFactor;

      if (dragTarget === 'left') {
        const raw = dragStartVal.current + scaledDelta;
        const snapped = snapToMm(Math.max(0, Math.min(raw, widthPx - marginRightPx - 40)));
        setDragLeftPx(snapped);
      } else {
        const raw = dragStartVal.current - scaledDelta;
        const snapped = snapToMm(Math.max(0, Math.min(raw, widthPx - marginLeftPx - 40)));
        setDragRightPx(snapped);
      }
    };

    const handleMouseUp = () => {
      if (dragTarget === 'left') {
        onMarginsChange?.({ left_mm: pxToMm(dragLeftPx) });
      } else {
        onMarginsChange?.({ right_mm: pxToMm(dragRightPx) });
      }
      setDragTarget(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragTarget, dragLeftPx, dragRightPx, widthPx, marginLeftPx, marginRightPx, onMarginsChange]);

  if (!visible) return null;

  // ------ Tick generation ------
  const unitPx = unit === 'inch' ? INCH_TO_PX : CM_TO_PX;
  const subDiv = unit === 'inch' ? 8 : 10; // subdivisions per major unit
  const totalUnits = Math.floor(widthPx / unitPx);
  const ticks: { x: number; type: 'major' | 'minor' | 'tiny'; label?: string }[] = [];

  for (let u = 0; u <= totalUnits; u++) {
    for (let s = 0; s < subDiv; s++) {
      const x = u * unitPx + s * (unitPx / subDiv);
      if (x > widthPx) break;

      if (s === 0) {
        ticks.push({ x, type: 'major', label: u > 0 ? String(u) : undefined });
      } else if (unit === 'cm' && s === 5) {
        ticks.push({ x, type: 'minor' });
      } else if (unit === 'inch' && s === 4) {
        ticks.push({ x, type: 'minor' });
      } else {
        ticks.push({ x, type: 'tiny' });
      }
    }
  }

  const tickHeight = (type: 'major' | 'minor' | 'tiny') => {
    if (type === 'major') return 10;
    if (type === 'minor') return 6;
    return 3;
  };

  return (
    <div className="relative mx-auto select-none" style={{ width: widthPx }}>
      {/* Ruler bar */}
      <div
        ref={rulerRef}
        className="relative bg-muted border-b border-border overflow-hidden"
        style={{ width: widthPx, height: 24 }}
      >
        {/* Lighter content zone between margins */}
        <div
          className="absolute top-0 bottom-0 bg-background/80"
          style={{
            left: currentLeft,
            right: currentRight,
          }}
        />

        {/* Tick marks */}
        <svg
          className="absolute inset-0"
          width={widthPx}
          height={24}
          style={{ pointerEvents: 'none' }}
        >
          {ticks.map((tick, i) => {
            const h = tickHeight(tick.type);
            return (
              <g key={i}>
                <line
                  x1={tick.x}
                  y1={24 - h}
                  x2={tick.x}
                  y2={24}
                  className="stroke-muted-foreground/60"
                  strokeWidth={tick.type === 'major' ? 1 : 0.5}
                />
                {tick.label && (
                  <text
                    x={tick.x}
                    y={11}
                    textAnchor="middle"
                    className="fill-muted-foreground"
                    style={{ fontSize: 9, fontFamily: 'sans-serif' }}
                  >
                    {tick.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Left margin marker */}
        <div
          className="absolute top-0 bottom-0 flex items-end cursor-ew-resize z-20 group"
          style={{ left: currentLeft - 6, width: 12 }}
          onMouseDown={(e) => handleMouseDown('left', e)}
        >
          <svg
            width={12}
            height={10}
            viewBox="0 0 12 10"
            className="fill-primary group-hover:fill-primary/80 transition-colors"
          >
            <polygon points="6,0 12,10 0,10" />
          </svg>
        </div>

        {/* Right margin marker */}
        <div
          className="absolute top-0 bottom-0 flex items-end cursor-ew-resize z-20 group"
          style={{ right: currentRight - 6, width: 12 }}
          onMouseDown={(e) => handleMouseDown('right', e)}
        >
          <svg
            width={12}
            height={10}
            viewBox="0 0 12 10"
            className="fill-primary group-hover:fill-primary/80 transition-colors"
          >
            <polygon points="6,0 12,10 0,10" />
          </svg>
        </div>
      </div>

      {/* Vertical guide lines while dragging */}
      {dragTarget === 'left' && (
        <div
          className="absolute border-l border-dashed border-primary/50 pointer-events-none z-30"
          style={{ left: currentLeft, top: 24, bottom: 0, height: 2000 }}
        />
      )}
      {dragTarget === 'right' && (
        <div
          className="absolute border-l border-dashed border-primary/50 pointer-events-none z-30"
          style={{ right: currentRight, top: 24, bottom: 0, height: 2000 }}
        />
      )}
    </div>
  );
}
